import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Anchor, Crown, Compass, ShieldCheck, ExternalLink } from "lucide-react";
import { NAV } from "@/constants/testIds";

const links = [
  { to: "/", label: "Contest", testId: NAV.linkContest },
  { to: "/leaderboard", label: "Leaderboard", testId: NAV.linkLeaderboard },
  { to: "/storefront", label: "Glow Storefront", testId: NAV.linkStorefront },
  { to: "/stream", label: "Siren Stream", testId: NAV.linkStream },
  { to: "/college", label: "Beauty War College", testId: NAV.linkCollege },
  { to: "/social", label: "Social Command", testId: NAV.linkSocial },
];

const FLEET_NODES = [
  {
    label: "Island Gold",
    href: "https://eigoo-command.preview.emergentagent.com/?utm_source=fleet&utm_medium=alliance&utm_campaign=flagship",
    icon: Crown,
    testid: "fleet-island-gold",
  },
  {
    label: "Siren Hub",
    href: "https://siren-storefront.preview.emergentagent.com/?utm_source=fleet&utm_medium=alliance&utm_campaign=siren",
    icon: Anchor,
    testid: "fleet-siren-hub",
  },
  {
    label: "Lead Sentry",
    href: "https://lead-sentry-gold.preview.emergentagent.com/?utm_source=fleet&utm_medium=alliance&utm_campaign=leadsentry",
    icon: ShieldCheck,
    testid: "fleet-lead-sentry",
  },
];

export const Layout = ({ children, onApply }) => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen flex flex-col">
      {/* EIGOO standards top ribbon */}
      <div
        data-testid="eigoo-ribbon"
        className="bg-[#C9A227] text-[#050505] text-[10px] tracking-[0.4em] uppercase font-black text-center py-1.5 px-4"
      >
        ⚓ Empowered by EIGOO Inc · EIGOO Standard Flagship Protocol
      </div>

      <header className="sticky top-0 z-50 bg-black/70 backdrop-blur-2xl border-b border-[#C9A227]/15">
        <div className="px-6 md:px-12 lg:px-20 h-16 md:h-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 group" data-testid={NAV.brand}>
            <Anchor className="text-[#C9A227] group-hover:rotate-12 transition-transform" size={20}/>
            <div className="flex flex-col leading-none">
              <span className="text-[10px] tracking-[0.32em] text-[#C9A227] uppercase">EIGOO Standard</span>
              <span className="text-white font-black text-base md:text-lg uppercase tracking-wider">Pacific Siren</span>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-6">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={l.testId}
                className={`text-[11px] tracking-[0.25em] uppercase font-bold transition-colors ${
                  pathname === l.to ? "text-[#C9A227]" : "text-white/70 hover:text-white"
                }`}>
                {l.label}
              </Link>
            ))}
            <Link to="/admiral" data-testid={NAV.linkAdmiral}
              className="text-[11px] tracking-[0.25em] uppercase font-bold text-white/40 hover:text-[#C9A227]">
              Admiral
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            {onApply && (
              <button onClick={onApply} className="btn-gold text-[10px] hidden md:inline-block" data-testid={NAV.applyCta}>
                Apply for Admiral
              </button>
            )}
            <button onClick={() => setOpen((v) => !v)} className="xl:hidden text-white/80">
              {open ? <X size={22}/> : <Menu size={22}/>}
            </button>
          </div>
        </div>

        {open && (
          <div className="xl:hidden border-t border-[#C9A227]/15 px-6 py-4 space-y-3 bg-black/95">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                className="block text-sm tracking-widest uppercase text-white/80 hover:text-[#C9A227]">
                {l.label}
              </Link>
            ))}
            <Link to="/admiral" onClick={() => setOpen(false)}
              className="block text-sm tracking-widest uppercase text-white/40 hover:text-[#C9A227]">
              Admiral Login
            </Link>
            {onApply && (
              <button onClick={() => { setOpen(false); onApply(); }} className="btn-gold w-full text-xs">
                Apply for Admiral Commission
              </button>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-[#C9A227]/15 mt-24">
        <div className="px-6 md:px-12 lg:px-20 py-10 grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Anchor className="text-[#C9A227]" size={18}/>
              <span className="text-white font-black uppercase tracking-wider">Pacific Siren</span>
            </div>
            <p className="text-white/40 text-xs leading-relaxed">
              An EIGOO Standard infrastructure. Casting call open for the Sovereign Admiral of the Pacific Siren.
            </p>
          </div>
          <div>
            <p className="label-gold mb-3">Modules</p>
            <ul className="space-y-2 text-xs text-white/60">
              <li>The Glow Storefront</li>
              <li>Siren Stream</li>
              <li>The Beauty War College</li>
              <li>Social Command</li>
              <li>Admiral Dashboard</li>
              <li>Armada Sector Catalog</li>
            </ul>
          </div>
          <div>
            <p className="label-gold mb-3">Doctrine</p>
            <p className="text-white/60 text-xs leading-relaxed">
              60 / 30 / 10 Lineage Law · Sovereign Roster · Mililani, Hawaii.<br/>
              <span className="text-[#C9A227]">(Oversight by Akanksha and Director K)</span><br/>
              © EIGOO Inc.
            </p>
          </div>
        </div>

        {/* Fleet Alliance Signal-Links */}
        <div className="border-t border-[#C9A227]/15 px-6 md:px-12 lg:px-20 py-8">
          <p className="label-gold mb-4 flex items-center gap-2">
            <Compass size={12} className="text-[#C9A227]"/> Fleet Alliance · Signal Links
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {FLEET_NODES.map((node) => {
              const Icon = node.icon;
              return (
                <a
                  key={node.label}
                  href={node.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={node.testid}
                  className="group flex items-center justify-between px-4 py-3 border border-[#C9A227]/25 hover:border-[#C9A227] hover:bg-[#C9A227]/5 transition-colors"
                >
                  <span className="flex items-center gap-3">
                    <Icon size={14} className="text-[#C9A227]"/>
                    <span className="text-[11px] tracking-[0.25em] uppercase text-white/80 group-hover:text-white font-bold">
                      {node.label}
                    </span>
                  </span>
                  <ExternalLink size={12} className="text-[#C9A227]/60 group-hover:text-[#C9A227]"/>
                </a>
              );
            })}
          </div>
        </div>

        {/* EMPOWERED BY EIGOO INC — sovereign signature */}
        <div
          data-testid="eigoo-signature"
          className="border-t border-[#C9A227]/15 px-6 md:px-12 lg:px-20 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 bg-black/40"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#C9A227] flex items-center justify-center">
              <Anchor size={16} className="text-[#050505]"/>
            </div>
            <span className="text-[#C9A227] text-[11px] tracking-[0.4em] uppercase font-black">
              Empowered by EIGOO Inc
            </span>
          </div>
          <span className="text-white/30 text-[10px] tracking-[0.3em] uppercase">
            Sovereign Beauty Infrastructure · MMXXVI
          </span>
        </div>
      </footer>
    </div>
  );
};
